/**
 * Backup & Restore utility voor Slaaploods Verlofbeheer
 * Exporteert alle Firestore collecties als JSON backup.
 */
import { db } from '../config/firebase';
import { collection, getDocs, doc, getDoc, setDoc, addDoc, deleteDoc, serverTimestamp } from 'firebase/firestore';

// Alle Firestore collecties die gebackupt moeten worden
const COLLECTIONS = [
  'users', 'leaveRequests', 'overuren', 'holidays',
  'vakantiePerioden', 'roosterWijzigingen', 'jaararchief',
  'flexPlanning', 'flexUren', 'flexUitbetalingen',
  'verlofTypes', 'notificaties', 'pushTokens',
];

// Settings document (geen collectie maar specifiek document)
const SETTINGS_DOCS = [
  { collection: 'settings', id: 'menuConfig' },
];

/**
 * Maak een volledige backup van alle Firestore data
 * @param {boolean} includeUsers - Medewerkerdata meenemen (false voor white-label export)
 * @returns {object} Backup object met alle collecties
 */
export async function maakBackup(includeUsers = true) {
  const backup = {
    meta: {
      versie: '1.0',
      datum: new Date().toISOString(),
      app: 'Slaaploods Verlofbeheer',
      type: includeUsers ? 'volledig' : 'template',
    },
    collecties: {},
    settings: {},
  };

  // Exporteer alle collecties
  for (const col of COLLECTIONS) {
    if (!includeUsers && col === 'users') continue;
    if (!includeUsers && ['leaveRequests', 'overuren', 'jaararchief', 'flexUren', 'flexUitbetalingen', 'flexPlanning', 'notificaties', 'pushTokens'].includes(col)) continue;

    try {
      const snap = await getDocs(collection(db, col));
      backup.collecties[col] = snap.docs.map(d => ({
        id: d.id,
        ...d.data(),
      }));
    } catch (err) {
      console.warn(`Backup ${col} mislukt:`, err);
      backup.collecties[col] = [];
    }
  }

  // Exporteer settings documenten
  for (const setting of SETTINGS_DOCS) {
    try {
      const snap = await getDoc(doc(db, setting.collection, setting.id));
      if (snap.exists()) {
        backup.settings[setting.id] = snap.data();
      }
    } catch (err) {
      console.warn(`Backup settings/${setting.id} mislukt:`, err);
    }
  }

  return backup;
}

/**
 * Download backup als JSON bestand
 */
export async function downloadBackup(includeUsers = true) {
  const backup = await maakBackup(includeUsers);
  const type = includeUsers ? 'volledig' : 'template';
  const datum = new Date().toISOString().split('T')[0];
  const filename = `slaaploods-backup-${type}-${datum}.json`;

  const blob = new Blob([JSON.stringify(backup, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);

  return { filename, size: blob.size, records: Object.values(backup.collecties).reduce((sum, arr) => sum + arr.length, 0) };
}

/**
 * Herstel backup vanuit JSON bestand
 * ⚠️ WAARSCHUWING: Overschrijft bestaande data!
 * @param {File} file - JSON backup bestand
 * @param {Function} onProgress - Callback voor voortgang
 */
export async function herstelBackup(file, onProgress = () => {}) {
  const text = await file.text();
  const backup = JSON.parse(text);

  if (!backup.meta || !backup.collecties) {
    throw new Error('Ongeldig backup bestand — meta of collecties ontbreken');
  }

  const totaal = Object.values(backup.collecties).reduce((sum, arr) => sum + arr.length, 0);
  let verwerkt = 0;

  for (const [colName, records] of Object.entries(backup.collecties)) {
    onProgress({ fase: `${colName} herstellen...`, verwerkt, totaal });

    for (const record of records) {
      try {
        const { id, ...data } = record;
        // Gebruik origineel ID als het een string is
        if (id) {
          await setDoc(doc(db, colName, id), { ...data, restoredAt: serverTimestamp() });
        } else {
          await addDoc(collection(db, colName), { ...data, restoredAt: serverTimestamp() });
        }
        verwerkt++;
      } catch (err) {
        console.warn(`Herstel ${colName}/${record.id} mislukt:`, err);
        verwerkt++;
      }
    }
  }

  // Herstel settings
  if (backup.settings) {
    for (const [settingId, data] of Object.entries(backup.settings)) {
      try {
        await setDoc(doc(db, 'settings', settingId), { ...data, restoredAt: serverTimestamp() });
      } catch (err) {
        console.warn(`Herstel settings/${settingId} mislukt:`, err);
      }
    }
  }

  onProgress({ fase: 'Klaar!', verwerkt, totaal });
  return { verwerkt, totaal };
}
